import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  ArrowIcon,
  Button,
  colors,
  LabeledCheckbox,
  Popup,
  PopupHeader,
  Row,
  Text,
} from "@commonsku/styles";

import { PurchaseOrder } from "../../types/bills";

import { FormPopupActions, FormPopupForm } from "../FormPopup";

import { oauth } from "../../utils";

const UNBILLED_PURCHASE_ORDER_POPUP_WIDTH = '36rem';

const UNBILLED_PURCHASE_ORDER_POPUP_STYLE = {
  display: "flex",
  flexFlow: "column nowrap",
  justifyContent: "flex-start",
  gap: "1rem",
  maxHeight: "80vh",
  minHeight: "16rem",
  height: "auto",
};

const UNBILLED_PURCHASE_ORDER_POPUP_CONTENT_STYLE = {
  display: "flex",
  flexFlow: "column nowrap",
  justifyContent: "space-between",
  flexGrow: 1,
  gap: "1rem",
  overflowY: "auto",
} as React.CSSProperties;

const StyledHeader = styled(PopupHeader)`
  font-size: 24px;
  font-weight: 600;
  line-height: 40px;
  display: flex;
  align-items: center;

  span {
    color: ${colors.neutrals["90"]};
    flex-grow: 1;
    margin-left: 1rem;
    padding-top: 3px;
  }

  svg {
    cursor: pointer;
    font-size: 32px;
  }
`;

const Header = ({
  showBack,
  title,
  onBack,
}: {
  showBack: boolean;
  title: React.ReactNode;
  onBack: () => void;
}) => (
  <StyledHeader>
    {showBack ? (
      <ArrowIcon
        size="large"
        direction="left"
        altText="back"
        onClick={onBack}
      />
    ) : null}
    <Text>{title}</Text>
  </StyledHeader>
);

export default function UnbilledPurchaseOrdersPopup({
  showBack,
  purchaseOrders,
  onClose,
  onBack,
}: {
  showBack: boolean;
  purchaseOrders: PurchaseOrder[];
  onClose: () => void;
  onBack: () => void;
}) {
  const [selected, setSelected] = useState<PurchaseOrder[]>([]);

  const handleMarkAsBilled = useCallback(async () => {
    try {
      await Promise.all(
        selected.map((po) =>
          oauth("PUT", `purchaseorder/${po.id}`, { billed: 1 })
        )
      );
    } catch (e) {
      toast.error("Unable to mark PO as billed");
    }
    onClose();
  }, [selected, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleToggleSelected = useCallback(
    (po: PurchaseOrder) => {
      if (selected.includes(po)) {
        setSelected(selected.filter((value) => value !== po));
      } else {
        setSelected(selected.concat(po));
      }
    },
    [selected]
  );

  return (
    <Popup
      closeOnClickOutside={false}
      width={UNBILLED_PURCHASE_ORDER_POPUP_WIDTH}
      style={UNBILLED_PURCHASE_ORDER_POPUP_STYLE}
      popupContentStyle={UNBILLED_PURCHASE_ORDER_POPUP_CONTENT_STYLE}
      header={
        <Header
          title="Unbilled Purchase Orders"
          onBack={onBack}
          showBack={showBack}
        />
      }
      onClose={handleClose}
    >
      <FormPopupForm>
        {purchaseOrders.map((po) => (
          <Row key={po.id}>
            <LabeledCheckbox
              checked={selected.includes(po)}
              label={`PO#${po.number} ${po.division.name} ${
                po.destination ? ` > ${po.destination.name}` : ""
              }`}
              onChange={() => handleToggleSelected(po)}
            />
          </Row>
        ))}
      </FormPopupForm>
      <FormPopupActions>
        <Button
          secondary
          disabled={selected.length === 0}
          onClick={handleMarkAsBilled}
        >
          Mark Selected POs as Billed
        </Button>
        <Button onClick={onClose}>Continue</Button>
      </FormPopupActions>
    </Popup>
  );
}
